import { FC } from 'react'
import {
  DefaultFormContainer,
  FormItemTitle,
  FormContentContainer,
  FlexDiv,
  MapFormContainer
} from './styles'
import StepBullet from '../../basic/StepBullet'
import { LeftBar } from '../../basic/LeftBar'
import StandardInput from '../../basic/StandardInput'
import StandardFormatInput from '../../../components/basic/StandardFormatInput'
import StandardSelect from '../../../components/basic/StandardSelect'
import { InsuranceData } from '../../../dtos/applicationData'
import OptionDateRange from './OptionDateRange'
import CurrentInsuranceInfo from './CurrentInsuranceInfo'
import { previousLiabilityLimits, previousLiabilityDeductibles } from 'src/constants/application'
import FileData from 'src/dtos/fileData'
import { numberToCurrency } from 'src/utils/numberFormat'
import { validateDateField } from 'src/utils/formDataManipulation'

interface LastInsuranceProps {
  title: string;
  insurance: InsuranceData;
  setInsurance: (insurance: InsuranceData) => void;
  insuranceNumber: number;
  isSecondary?: boolean;
  handleUploadFile: (fileData: FileData[] | undefined) => Promise<void>;
  isIncorrect?: boolean;
  errorCodes?: string[];
}

const LastInsurance: FC<LastInsuranceProps> = ({ 
  title,
  insurance,
  setInsurance,
  insuranceNumber,
  isSecondary,
  handleUploadFile,
  isIncorrect,
  errorCodes = [''],
}) => {

  return (
    <MapFormContainer >
      <DefaultFormContainer>
        <FlexDiv>
          <StepBullet>{insuranceNumber}</StepBullet>
          <FlexDiv dir="column" maxWidth='460px'>
            <FormItemTitle>{title}</FormItemTitle>
            <FormContentContainer btnMaxWidth='70%'>
              <LeftBar />
              <div className='content'>
                <StandardInput
                  label='Insurance Company'
                  value={insurance?.companyName ?? ''}
                  onChange={e => setInsurance({...insurance, companyName: e.target.value}) }
                  errorCode={`${errorCodes[0]}:${insuranceNumber-1}`}
                />
                <StandardSelect
                  label='Limits of Liability'
                  value={insurance?.liabilityLimits ?? ''}
                  onChange={e => setInsurance({...insurance, liabilityLimits: e.target.value})}
                  errorCode={`${errorCodes[1]}:${insuranceNumber-1}`}
                >
                  <option value="" disabled>Select</option>
                  {previousLiabilityLimits.map((pt, index) => (
                    <option key={index} value={pt}>{pt}</option>
                  ))}
                </StandardSelect>
                <FlexDiv gap="24px">
                  <LeftBar />
                  <StandardSelect
                    label='Deductible'
                    value={insurance?.deductible ?? ''}
                    onChange={e => setInsurance({...insurance, deductible: (!isNaN(Number(e.target.value)) ? Number(e.target.value) : 0)})}
                    errorCode={`${errorCodes[2]}:${insuranceNumber-1}`}
                  >
                    <option value="0" disabled>Select</option>
                    {previousLiabilityDeductibles.map((deductible, index) => (
                      <option key={index} value={deductible}>{numberToCurrency((deductible/1000), 0, 0)}k</option>
                    ))}
                  </StandardSelect>
                </FlexDiv>
                <StandardFormatInput
                  label="Expiration Date"
                  labelSize='small'
                  maskStyle='date'
                  value={insurance?.expirationDate ?? undefined}
                  onValueChange={({formattedValue}) => setInsurance({...insurance, expirationDate: formattedValue})}
                  isIncorrect={isIncorrect}
                  errorMessage={isIncorrect ? validateDateField(insurance?.expirationDate, true) : ''}
                  errorCode={`${errorCodes[3]}:${insuranceNumber-1}`}
                />
                <StandardFormatInput
                  label='Annual Premium'
                  value={insurance?.annualPremium}
                  maskStyle="number"
                  thousandSeparator=","
                  leftIcon='currency'
                  onValueChange={({ floatValue }) => setInsurance({...insurance, annualPremium: floatValue ?? undefined})}
                  errorCode={`${errorCodes[4]}:${insuranceNumber-1}`}
                />
                <OptionDateRange
                  title='Did you purchase any tail coverage for this policy?'
                  subtitle='Dates of Tail Coverage (Optional)'
                  showOptions={true}
                  from={insurance.tailCoverageFromDate}
                  setFrom={(from) => { setInsurance({...insurance, tailCoverageFromDate: from}) }}
                  to={insurance.tailCoverageToDate}
                  setTo={(to) => { setInsurance({...insurance, tailCoverageToDate: to}) }}
                  selectedOption={insurance.isTailCoverage}
                  setSelectedOption={(option) => { setInsurance({...insurance, isTailCoverage: option}) }}
                  errorCodes={[`${errorCodes[5]}:${insuranceNumber-1}`, `${errorCodes[6]}:${insuranceNumber-1}`, `${errorCodes[7]}:${insuranceNumber-1}`]}
                />
                {!isSecondary ?
                  <CurrentInsuranceInfo
                    retroactiveDate={insurance.retroactiveDate}
                    fileData={insurance.policyFile}
                    handleUploadFile={response => handleUploadFile(response)}
                    selectedOption={insurance.isCurrent}
                    insurance={insurance}
                    setInsurance={(insurance) => setInsurance(insurance)}
                    isIncorrect={isIncorrect && (insurance.isCurrent === null || insurance.isCurrent === undefined)}
                    errorCodes={[`${errorCodes[8]}:${insuranceNumber-1}`, `${errorCodes[9]}:${insuranceNumber-1}`]}
                  />
                  : undefined}
              </div>
            </FormContentContainer>
          </FlexDiv>
        </FlexDiv>
      </DefaultFormContainer>
    </MapFormContainer>
  )
}

export default LastInsurance
